import type { DatumAbstraction, LevelDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import {
  type ResolvedDatumTable,
  type ResolvedBuildingDatumHandle,
  GRADE_DATUM_ID,
} from "building-types/datums/datumsTypes";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { DatumHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { RWTable, Table } from "common/data/table";
import { levelInchesToWorld } from "common/utils/nominalNumbers";
import { planeXYAtZ } from "geom/plane";

export function getLevelDatumAbstractions(
  datumAbstractions: Table<DatumAbstraction>,
  levelId: string | undefined,
): Table<LevelDatumAbstraction> {
  const levelDatumAbstractions: RWTable<LevelDatumAbstraction> = {};

  for (const datumId in datumAbstractions) {
    const datumAbstraction = datumAbstractions[datumId];
    if (!datumAbstraction.isValidForUsage(DatumVerticalUsageType.LEVEL) || !datumAbstraction.isValidForLevel(levelId)) {
      continue;
    }

    levelDatumAbstractions[datumId] = datumAbstraction;
  }

  return levelDatumAbstractions;
}

export function collectFromLevels(
  levels: Table<Level>,
  levelsAttributes: Table<LevelAttributes>,
  datumAbstractions: Table<DatumAbstraction>,
): ResolvedDatumTable {
  let resolvedDatums: ResolvedDatumTable = {};
  for (const level of Object.values(levels)) {
    const lz = level.floorElevation;
    resolvedDatums[level.id] = {
      name: level.name,
      id: level.id,
      source: level.handle,
      plane: planeXYAtZ(lz),
    };
    resolvedDatums = collectFromLevelDatums(resolvedDatums, level, levelsAttributes[level.id], datumAbstractions);
  }
  return resolvedDatums;
}

function collectFromLevelDatums(
  resolvedDatums: ResolvedDatumTable,
  level: Level,
  levelAttributes: LevelAttributes,
  datumAbstractions: Table<DatumAbstraction>,
): ResolvedDatumTable {
  const levelDatumAbstractions = getLevelDatumAbstractions(datumAbstractions, level.id);

  for (const datumId in levelDatumAbstractions) {
    const levelDatumAbstraction = levelDatumAbstractions[datumId];

    const id = levelDatumAbstraction.getId();
    const name = levelDatumAbstraction.getName();

    // Ignore the anchor for level datums to be consistent with the existing behavior
    // TODO: If we want to use the anchor for level datums here, we'll need to run a migration to mark
    // existing level datums to not use the anchor, and make newly created level datums to use the anchor.
    const ldz = levelDatumAbstraction.resolveVerticalDatum({ level, levelAttributes, ignoreAnchor: true });

    resolvedDatums[id] = {
      name,
      id,
      source: {
        type: "DATUM",
        id,
      } as DatumHandle,
      plane: planeXYAtZ(ldz),
    };
  }

  for (const [name, value] of Object.entries(level.levelDatumsGenerated)) {
    const id = getResolvedDatumId(level.handle, name);
    const lgz = levelInchesToWorld(value, level.floorElevation);
    if (name === "grade") {
      if (!resolvedDatums[GRADE_DATUM_ID]) {
        resolvedDatums[GRADE_DATUM_ID] = {
          name: "grade",
          id: GRADE_DATUM_ID,
          source: null,
          plane: planeXYAtZ(lgz),
        };
      }
      continue;
    }

    resolvedDatums[id] = {
      name: `${level.name} > ${name}`,
      id,
      source: {
        type: "DATUM",
        id,
      } as DatumHandle,
      plane: planeXYAtZ(lgz),
    };
  }
  return resolvedDatums;
}

function getResolvedDatumId(handle: ResolvedBuildingDatumHandle, name?: string): string {
  return getDatumId(handle) + (name ? "-" + name : "");
}

function getDatumId(handle: ResolvedBuildingDatumHandle): string | undefined {
  switch (handle?.type) {
    case "LEVEL":
      return handle.id;
    case "ROOM":
      return handle.id;
    case "DATUM":
      return handle.id;
    case "STAIR_BLOCK":
      return handle.id;
    default:
      return undefined;
  }
}
